import { supabase } from '../lib/supabase';
import { listarPendientes, actualizarPendiente, borrarPendiente, contarPendientes } from './db';

// Ejecuta un movimiento mover_lpn. Idempotente por (tenant_id, client_uuid) en la base.
export async function ejecutarMover({ tenant_id, lpn_codigo, ubicacion_codigo, client_uuid }) {
  const { data, error } = await supabase.rpc('mover_lpn', {
    p_tenant: tenant_id,
    p_lpn_codigo: lpn_codigo,
    p_ubicacion_codigo: ubicacion_codigo,
    p_client_uuid: client_uuid,
  });
  if (error) throw error;
  return data;
}

// Fase 3: traspaso entre almacenes (salida del origen + entrada en destino). Idempotente por client_uuid.
export async function ejecutarTraspaso(p) {
  const { data, error } = await supabase.rpc('traspaso_almacen', {
    p_tenant: p.tenant_id,
    p_lpn_codigos: p.lpn_codigos,
    p_ubicacion_destino: p.ubicacion_destino,
    p_client_uuid: p.client_uuid,
    p_motivo: p.motivo ?? null,
  });
  if (error) throw error;
  return data;
}

let sincronizando = false;

// Registra un palet del inventario inicial. Idempotente por (tenant_id, client_uuid) en la base.
export async function ejecutarInventarioPalet(p) {
  const { data, error } = await supabase.rpc('inventario_registrar_palet', {
    p_tenant: p.tenant_id,
    p_client_uuid: p.client_uuid,
    p_ubicacion_codigo: p.ubicacion_codigo,
    p_lpn_codigo: p.lpn_codigo,
    p_articulo_sku: p.articulo_sku,
    p_cantidad: Number(p.cantidad),
    p_lote: p.lote || '',
    p_caducidad: p.caducidad || null,
    p_cliente: p.cliente || null,
  });
  if (error) throw error;
  return data;
}

// Marca una ubicación como comprobada (vacía o no) durante el inventario inicial.
export async function ejecutarInventarioVacio(p) {
  const { data, error } = await supabase.rpc('inventario_marcar_ubicacion', {
    p_tenant: p.tenant_id,
    p_ubicacion_codigo: p.ubicacion_codigo,
    p_vacio: p.vacio !== false,
  });
  if (error) throw error;
  return data;
}

// Fase 2: confirma la mercancía de un palet (crea contenido + movimiento ENTRADA + ubica). Idempotente por client_uuid.
export async function ejecutarConfirmar(p) {
  const { data, error } = await supabase.rpc('confirmar_palet', {
    p_tenant: p.tenant_id,
    p_lpn_codigo: p.lpn_codigo,
    p_client_uuid: p.client_uuid,
    p_cantidad: p.cantidad ?? null,
    p_ubicacion_codigo: p.ubicacion_codigo ?? null,
    p_lote: p.lote ?? null,
    p_caducidad: p.caducidad ?? null,
    p_proveedor: p.proveedor ?? null,
    p_codigo_externo: p.codigo_externo ?? null,
  });
  if (error) throw error;
  return data;
}

// Fase 2: pasa palets a la playa de muelle (salen del rack; el stock sigue contando). Idempotente por client_uuid.
export async function ejecutarMuelle(p) {
  const { data, error } = await supabase.rpc('poner_en_muelle', {
    p_tenant: p.tenant_id,
    p_lpn_codigos: p.lpn_codigos,
    p_client_uuid: p.client_uuid,
    p_pedido_id: p.pedido_id || null,
  });
  if (error) throw error;
  return data;
}

// Fase 2: confirma la carga (expide) los palets del pedido. Idempotente por client_uuid.
export async function ejecutarServir(p) {
  const { data, error } = await supabase.rpc('servir_pedido', {
    p_pedido_id: p.pedido_id,
    p_lpn_codigos: p.lpn_codigos,
    p_client_uuid: p.client_uuid,
    p_cerrar_resto: !!p.cerrar_resto,
  });
  if (error) throw error;
  return data;
}

// Reintenta la cola con backoff exponencial. Un rechazo del servidor NO se descarta:
// se marca "rechazado" con el motivo y queda visible en /pendientes.
export async function sincronizar(onCambio) {
  if (sincronizando || !navigator.onLine) return;
  sincronizando = true;
  try {
    const cola = await listarPendientes();
    for (const p of cola) {
      if (p.estado === 'rechazado') continue;
      try {
        if (p.accion === 'inventario_palet') await ejecutarInventarioPalet(p);
        else if (p.accion === 'inventario_vacio') await ejecutarInventarioVacio(p);
        else if (p.accion === 'muelle') await ejecutarMuelle(p);
        else if (p.accion === 'servir') await ejecutarServir(p);
        else if (p.accion === 'confirmar') await ejecutarConfirmar(p);
        else if (p.accion === 'traspaso') await ejecutarTraspaso(p);
        else await ejecutarMover(p);
        await borrarPendiente(p.client_uuid);
      } catch (e) {
        const intentos = (p.intentos || 0) + 1;
        const motivo = (e && (e.message || e.detail)) || 'error';
        const rechazadoDefinitivo = /HUECO:|no existe|sin capacidad|bloque/i.test(motivo);
        await actualizarPendiente(p.client_uuid, {
          intentos,
          estado: rechazadoDefinitivo || intentos >= 5 ? 'rechazado' : 'pendiente',
          motivo,
        });
      }
    }
  } finally {
    sincronizando = false;
    if (onCambio) onCambio(await contarPendientes());
  }
}

let backoff = 2000;
export function arrancarAutoSync(onCambio) {
  const intentar = async () => {
    await sincronizar(onCambio);
  };
  window.addEventListener('online', () => { backoff = 2000; intentar(); });
  const bucle = async () => {
    if (navigator.onLine) {
      const antes = await contarPendientes();
      await intentar();
      const despues = await contarPendientes();
      backoff = despues < antes ? 2000 : Math.min(backoff * 2, 60000);
    }
    setTimeout(bucle, backoff);
  };
  setTimeout(bucle, 3000);
}
